import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, TrendingUp, Clock, CheckCircle, AlertCircle } from "lucide-react";
import { formatCurrency, type Currency } from "@/lib/currency";

interface Participant {
  id: string;
  email: string;
  has_paid: boolean;
}

interface Request {
  id: string;
  product_name: string;
  total_price: number;
  status: string;
  created_at: string;
  deadline: string;
  participants: Participant[];
}

interface ClientTrendsProps {
  requests: Request[];
  currency: Currency;
}

interface ClientStats {
  email: string;
  totalPaid: number;
  totalCollections: number;
  paidCount: number;
  unpaidCount: number;
  lastActivity: Date;
  paymentRate: number;
}

export function ClientTrends({ requests, currency }: ClientTrendsProps) {
  const clientData = useMemo(() => {
    const clientMap: Record<string, ClientStats> = {};

    requests.forEach(r => {
      r.participants.forEach(p => {
        const email = p.email.toLowerCase();
        if (!clientMap[email]) {
          clientMap[email] = {
            email,
            totalPaid: 0,
            totalCollections: 0,
            paidCount: 0,
            unpaidCount: 0,
            lastActivity: new Date(r.created_at),
            paymentRate: 0,
          };
        }
        const client = clientMap[email];
        client.totalCollections++;
        if (p.has_paid) {
          client.paidCount++;
          client.totalPaid += r.total_price / r.participants.length;
        } else {
          client.unpaidCount++;
        }
        const rDate = new Date(r.created_at);
        if (rDate > client.lastActivity) client.lastActivity = rDate;
      });
    });

    Object.values(clientMap).forEach(c => {
      c.paymentRate = c.totalCollections > 0 ? Math.round((c.paidCount / c.totalCollections) * 100) : 0;
    });

    const clients = Object.values(clientMap).sort((a, b) => b.totalPaid - a.totalPaid);
    
    // Summary stats
    const totalClients = clients.length;
    const avgPaymentRate = totalClients > 0
      ? Math.round(clients.reduce((s, c) => s + c.paymentRate, 0) / totalClients)
      : 0;
    const repeatClients = clients.filter(c => c.totalCollections > 1).length;
    const topSpender = clients[0];

    return { clients, totalClients, avgPaymentRate, repeatClients, topSpender };
  }, [requests]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Client Trends</h2>
        <p className="text-sm text-muted-foreground">Understand your client payment patterns</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground mb-1">Total Clients</p>
            <p className="text-2xl font-bold">{clientData.totalClients}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground mb-1">Avg Payment Rate</p>
            <p className="text-2xl font-bold">{clientData.avgPaymentRate}%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground mb-1">Repeat Clients</p>
            <p className="text-2xl font-bold">{clientData.repeatClients}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground mb-1">Top Spender</p>
            <p className="text-sm font-bold truncate">{clientData.topSpender?.email || '—'}</p>
            {clientData.topSpender && (
              <p className="text-xs text-muted-foreground">{formatCurrency(clientData.topSpender.totalPaid, currency)}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Client List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">All Clients</CardTitle>
          <CardDescription>Sorted by total payment amount</CardDescription>
        </CardHeader>
        <CardContent>
          {clientData.clients.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No client data yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {clientData.clients.slice(0, 20).map((client) => (
                <div key={client.email} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{client.email}</p>
                    <p className="text-xs text-muted-foreground">
                      {client.totalCollections} collection{client.totalCollections !== 1 ? 's' : ''} • {client.paidCount} paid
                    </p>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <Badge 
                      variant="outline" 
                      className={client.paymentRate >= 80 ? "text-emerald-600 border-emerald-300" : client.paymentRate >= 50 ? "text-amber-600 border-amber-300" : "text-red-600 border-red-300"}
                    >
                      {client.paymentRate}% rate
                    </Badge>
                    <span className="text-sm font-semibold">{formatCurrency(client.totalPaid, currency)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
